import React from 'react';
import { Box, Skeleton, Typography } from '@mui/material';
import PlatformLogo from './PlatformLogo';
import { useBranding } from '../../contexts/BrandingContext';

const shards = [
  { clip: 'polygon(0 0,35% 0,28% 36%,0 42%)', x: -20, y: -13, r: -9 },
  { clip: 'polygon(35% 0,68% 0,62% 34%,28% 36%)', x: 2, y: -22, r: 7 },
  { clip: 'polygon(68% 0,100% 0,100% 40%,62% 34%)', x: 21, y: -11, r: 10 },
  { clip: 'polygon(0 42%,28% 36%,36% 69%,0 73%)', x: -23, y: 2, r: 6 },
  { clip: 'polygon(28% 36%,62% 34%,68% 67%,36% 69%)', x: 0, y: 0, r: -5 },
  { clip: 'polygon(62% 34%,100% 40%,100% 75%,68% 67%)', x: 24, y: 2, r: -8 },
  { clip: 'polygon(0 73%,36% 69%,38% 100%,0 100%)', x: -17, y: 16, r: -9 },
  { clip: 'polygon(36% 69%,68% 67%,66% 100%,38% 100%)', x: 1, y: 22, r: 8 },
  { clip: 'polygon(68% 67%,100% 75%,100% 100%,66% 100%)', x: 20, y: 16, r: 9 },
];

function ShatterLogo({ compact, micro, logo, name }) {
  const width = micro ? 42 : (compact ? 72 : 96);
  const height = micro ? 30 : (compact ? 52 : 68);

  if (!logo) {
    return <Box sx={{ animation: 'tfShatterFallback 1.55s ease-in-out infinite' }}><PlatformLogo width={width} height={height} radius={12} fit="contain" padding="5%" /></Box>;
  }

  return (
    <Box sx={{ width, height, position: 'relative', filter: 'drop-shadow(0 9px 16px rgba(24,72,91,.14))' }} aria-label={`${name || 'TELCOFAIR'} loading`}>
      {shards.map((piece, index) => (
        <Box
          key={piece.clip}
          component="img"
          src={logo}
          alt=""
          aria-hidden="true"
          sx={{
            position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain', objectPosition: 'center',
            clipPath: piece.clip, WebkitClipPath: piece.clip,
            animation: 'tfLogoShatter 1.65s cubic-bezier(.22,.8,.25,1) infinite',
            animationDelay: `${index * 18}ms`,
            '--sx': `${piece.x}px`, '--sy': `${piece.y}px`, '--sr': `${piece.r}deg`,
            '--sx2': `${piece.x * 0.36}px`, '--sy2': `${piece.y * 0.36}px`, '--sr2': `${piece.r * 0.34}deg`,
          }}
        />
      ))}
    </Box>
  );
}

export function BrandedPageLoader({ label = 'Loading workspace…', compact = false, micro = false, skeleton = false }) {
  const brand = useBranding();
  if (skeleton) {
    return (
      <Box sx={{ p: { xs: 1.25, md: 2 }, width: '100%' }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,1fr)', xl: 'repeat(4,1fr)' }, gap: 1.2, mb: 1.3 }}>
          {[0, 1, 2, 3].map((key) => <Skeleton key={key} variant="rounded" height={110} sx={{ borderRadius: '18px', bgcolor: 'rgba(31,62,82,.06)' }} />)}
        </Box>
        <Skeleton variant="rounded" height={compact ? 220 : 360} sx={{ borderRadius: '20px', bgcolor: 'rgba(31,62,82,.055)' }} />
      </Box>
    );
  }

  if (micro) {
    return (
      <Box sx={{ width: 58, height: 46, display: 'grid', placeItems: 'center', position: 'relative', overflow: 'visible' }}>
        <Box sx={{ position: 'absolute', width: 64, height: 64, borderRadius: '50%', background: 'radial-gradient(circle, color-mix(in srgb,var(--primary,#2E6F8E) 12%,transparent), transparent 69%)', animation: 'tfLoaderGlowMicro 1.2s ease-in-out infinite' }} />
        <Box sx={{ position: 'relative', zIndex: 1 }}><ShatterLogo micro logo={brand.logo} name={brand.name} /></Box>
        <style>{`
          @keyframes tfLogoShatter{0%,14%{transform:translate(0,0) rotate(0deg) scale(1);opacity:1}31%{transform:translate(var(--sx),var(--sy)) rotate(var(--sr)) scale(.97);opacity:.94}51%{transform:translate(var(--sx2),var(--sy2)) rotate(var(--sr2)) scale(.99);opacity:1}67%,100%{transform:translate(0,0) rotate(0deg) scale(1);opacity:1}}
          @keyframes tfLoaderGlowMicro{0%,100%{transform:scale(.82);opacity:.45}45%{transform:scale(1.08);opacity:.9}}
          @media (prefers-reduced-motion: reduce){[style*="tfLogoShatter"]{animation:none!important}}
        `}</style>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: compact ? 158 : 320, width: '100%', display: 'grid', placeItems: 'center', position: 'relative', overflow: 'hidden' }}>
      <Box sx={{ textAlign: 'center', position: 'relative', display: 'grid', justifyItems: 'center' }}>
        <Box sx={{ position: 'absolute', width: compact ? 145 : 190, height: compact ? 145 : 190, top: '50%', left: '50%', transform: 'translate(-50%,-57%)', borderRadius: '50%', background: 'radial-gradient(circle, color-mix(in srgb,var(--primary,#2E6F8E) 11%,transparent), transparent 67%)', animation: 'tfLoaderGlow 1.65s ease-in-out infinite' }} />
        <Box sx={{ position: 'relative', zIndex: 1 }}><ShatterLogo compact={compact} logo={brand.logo} name={brand.name} /></Box>
        <Typography sx={{ mt: 1.25, color: '#3E4F65', fontWeight: 850, fontSize: compact ? '.69rem' : '.78rem' }}>{label}</Typography>
        <Typography sx={{ color: '#91A0B0', fontWeight: 800, fontSize: '.55rem', mt: .25, textTransform: 'uppercase', letterSpacing: '.12em' }}>{brand.name || 'TELCOFAIR'}</Typography>
      </Box>
      <style>{`
        @keyframes tfLogoShatter{
          0%,14%{transform:translate(0,0) rotate(0deg) scale(1);opacity:1}
          31%{transform:translate(var(--sx),var(--sy)) rotate(var(--sr)) scale(.97);opacity:.94}
          51%{transform:translate(var(--sx2),var(--sy2)) rotate(var(--sr2)) scale(.99);opacity:1}
          67%,100%{transform:translate(0,0) rotate(0deg) scale(1);opacity:1}
        }
        @keyframes tfShatterFallback{0%,15%,70%,100%{transform:scale(1);filter:blur(0)}35%{transform:scale(.92) rotate(-2deg);filter:blur(.2px)}50%{transform:scale(1.07) rotate(2deg)}}
        @keyframes tfLoaderGlow{0%,100%{transform:translate(-50%,-57%) scale(.86);opacity:.55}42%{transform:translate(-50%,-57%) scale(1.08);opacity:1}}
        @media (prefers-reduced-motion: reduce){[style*="tfLogoShatter"],[style*="tfShatterFallback"]{animation:none!important}}
      `}</style>
    </Box>
  );
}

export default BrandedPageLoader;